import { Link } from "react-router-dom";
import { Globe, Mail, MapPin, Phone } from "lucide-react";
import { useSiteData } from "@/context/SiteDataContext";
import { waLink } from "@/lib/api";
import { FullLogo } from "./Logo";
import { WhatsAppIcon } from "./WhatsAppIcon";
import { SocialIcons } from "./Social";

const QUICK = [["/", "Home"], ["/packages", "Tour Packages"], ["/reviews", "Guest Reviews"], ["/contact", "Contact"]];
const SERVICES = [["/ferry-booking", "Ferry Booking"], ["/hotel-booking", "Hotel Booking"], ["/water-activities", "Water Activities"]];
const PACKAGES = [["/andaman-honeymoon-packages", "Honeymoon Packages"], ["/andaman-family-packages", "Family Packages"], ["/andaman-budget-packages", "Budget Packages"], ["/andaman-premium-packages", "Premium Packages"], ["/andaman-luxury-packages", "Luxury Packages"]];

const Col = ({ title, links, id, extra }) => (
  <div>
    <p className="text-xs uppercase tracking-[0.25em] text-aqua font-semibold">{title}</p>
    <ul className="mt-5 space-y-2.5 text-sm">
      {links.map(([to, label]) => <li key={to}><Link to={to} className="hover:text-white transition-colors" data-testid={`footer-${id}-${label.toLowerCase().replace(/\s+/g, "-")}`}>{label}</Link></li>)}
      {extra}
    </ul>
  </div>
);

export const Footer = () => {
  const { settings } = useSiteData();
  return (
    <footer className="bg-navy text-slate-300 mt-24" data-testid="site-footer">
      <div className="container-x py-16 grid gap-12 sm:grid-cols-2 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <Link to="/" className="inline-block rounded-2xl bg-white p-3 shadow-lg" data-testid="footer-logo-link"><FullLogo className="w-32" /></Link>
          <p className="mt-6 text-sm leading-relaxed text-slate-400 max-w-sm">
            Andaman &amp; Nicobar Islands travel specialists: tour packages, ferry tickets and hotel stays across Port Blair, Havelock and Neil Island.
          </p>
          <p className="mt-6 text-xs uppercase tracking-[0.25em] text-aqua font-semibold">Follow us</p>
          <div className="mt-3"><SocialIcons prefix="footer" /></div>
        </div>
        <div className="lg:col-span-2"><Col title="Quick links" links={QUICK} id="quick"
          extra={settings.review_link && <li><a href={settings.review_link} target="_blank" rel="noreferrer" className="hover:text-white transition-colors" data-testid="footer-google-review-link">Review us on Google</a></li>} /></div>
        <div className="lg:col-span-2"><Col title="Services" links={SERVICES} id="service" /><div className="mt-8"><Col title="Packages" links={PACKAGES} id="package" /></div></div>
        <div className="lg:col-span-4">
          <p className="text-xs uppercase tracking-[0.25em] text-aqua font-semibold">Get in touch</p>
          <ul className="mt-5 space-y-3 text-sm">
            <li><a href={waLink(settings.whatsapp, "Hi Ocean Wings Travel!")} target="_blank" rel="noreferrer" className="flex items-center gap-3 hover:text-white" data-testid="footer-whatsapp-link"><WhatsAppIcon className="h-4 w-4 text-emerald-400" /> {settings.whatsapp}</a></li>
            <li className="flex items-center gap-3"><Globe className="h-4 w-4 text-aqua" /> {settings.website}</li>
            {settings.phone && <li className="flex items-center gap-3"><Phone className="h-4 w-4 text-aqua" /> {settings.phone}</li>}
            {settings.email && <li className="flex items-center gap-3"><Mail className="h-4 w-4 text-aqua" /> {settings.email}</li>}
            {settings.address && <li className="flex items-start gap-3"><MapPin className="h-4 w-4 text-aqua mt-0.5" /> {settings.address}</li>}
          </ul>
          <a href={waLink(settings.whatsapp, "Hi Ocean Wings Travel, I'd like to plan an Andaman trip.")} target="_blank" rel="noreferrer" data-testid="footer-whatsapp-cta"
            className="mt-6 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-emerald-500 text-white text-sm font-semibold hover:bg-emerald-600 transition-colors">
            <WhatsAppIcon className="h-4 w-4" /> Chat on WhatsApp
          </a>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="container-x py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
          <p>© {new Date().getFullYear()} {settings.company_name}. All rights reserved.</p>
          <Link to="/admin/login" className="hover:text-slate-300" data-testid="nav-link-admin-login">Admin login</Link>
        </div>
      </div>
    </footer>
  );
};
